/**
 * Tasks Service — afn-path-api's BP-compat task surface
 * (`/api/v1/application/{loanId}/tasks*`).
 *
 * The API returns the six status buckets already split per borrower, so there is
 * no client-side grouping here: the list read is a straight normalize of the wire
 * shape. The task-card upload has its own route (`.../tasks/{taskId}/upload`) and
 * its own three-field body — the task comes from the URL, and the entity the
 * document is filed against is derived server-side from that task, so nothing
 * about the association travels from here.
 *
 * The whole surface is gated behind the `TasksPage` feature flag server-side; with
 * it off every route 404s, which is also what the tab gate in `useVisibleTabs`
 * keys on.
 */

import { apiClient } from './api-client';
import { fileToBase64, toNumberOrUndefined } from './documents';
import { readUploadedId } from './docs';
import type {
  BpAttachmentItem,
  BpLoanTask,
  BpLoanTasksResponse,
  BpUploadTaskAttachmentResponse,
  BpUploadTaskDocumentItem,
  InProgressTask,
  TaskAttachment,
  TasksResult,
} from '@/types/tasks';

const BASE = '/api/v1/application';

/**
 * The API emits a task's `completedDate` and an attachment's `createdDate`
 * without a trailing `Z` — a faithful copy of the old backend, which stamped
 * both from a `DateTime` of unspecified kind. The values ARE UTC, but JS parses
 * an offset-less date-time as LOCAL, so rendering one straight would shift it by
 * the viewer's offset (the old app read these through an explicit `dayjs.utc`
 * for the same reason). Marking them here keeps `formatDateTimeValue` honest.
 */
function toUtcInstant(value: string | null | undefined): string | null {
  if (!value) return null;
  return /(?:Z|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`;
}

function normalizeAttachment(attachment: BpAttachmentItem): TaskAttachment {
  return {
    id: attachment.id ?? '',
    documentId: attachment.objectId ?? '',
    title: attachment.title ?? '',
    type: attachment.type ?? '',
    fileSize: toNumberOrUndefined(attachment.fileSize) ?? null,
    createdDate: toUtcInstant(attachment.createdDate),
  };
}

function normalizeTask(task: BpLoanTask): InProgressTask {
  return {
    // Every task carries both `id` and `taskId` with the same value; the wire
    // declares both nullable, so fall through rather than trusting one.
    taskId: task.taskId ?? task.id ?? '',
    loanGuid: task.loanGuid ?? '',
    borrowerType: Number(task.borrowerType ?? 0),
    taskName: task.taskName ?? '',
    taskKey: task.taskKey ?? '',
    dueDate: task.dueDate ?? null,
    dateAdded: task.dateAdded ?? null,
    taskStatus: Number(task.taskStatus ?? 0),
    dateModified: task.dateModified ?? null,
    completedDate: toUtcInstant(task.completedDate),
    externalFacingMessage: task.externalFacingMessage ?? '',
    objectiveName: task.objectiveName ?? '',
    requiredDocumentTypes: (task.requiredDocumentTypes ?? []).map((type) => ({
      id: type.id ?? '',
      name: type.name ?? '',
    })),
    attachments: (task.attachments ?? []).map(normalizeAttachment),
  };
}

/** The wire shape → what the Tasks page renders. Exported for fixtures and tests. */
export function normalizeTasks(response: BpLoanTasksResponse): TasksResult {
  const tasks = (bucket: BpLoanTask[] | undefined) => (bucket ?? []).map(normalizeTask);
  return {
    loanGuid: response.loanGuid ?? '',
    loanNumber: response.loanNumber ?? '',
    borrowerFirstName: response.borrowerFirstName ?? '',
    borrowerLastName: response.borrowerLastName ?? '',
    coBorrowerFirstName: response.coBorrowerFirstName ?? null,
    coBorrowerLastName: response.coBorrowerLastName ?? null,
    borrowerOpenTasks: tasks(response.borrowerOpenTasks),
    borrowerInProgressTasks: tasks(response.borrowerInProgressTasks),
    borrowerCompletedTasks: tasks(response.borrowerCompletedTasks),
    coBorrowerOpenTasks: tasks(response.coBorrowerOpenTasks),
    coBorrowerInProgressTasks: tasks(response.coBorrowerInProgressTasks),
    coBorrowerCompletedTasks: tasks(response.coBorrowerCompletedTasks),
  };
}

export const taskService = {
  async getTasks(loanId: string): Promise<TasksResult> {
    const response = await apiClient.get<BpLoanTasksResponse>(`${BASE}/${loanId}/tasks`);
    return normalizeTasks(response ?? {});
  },

  /**
   * One file against one task. The body is a bare array because the old backend
   * accepted N and looped, even though the UI has only ever posted one.
   *
   * `documentType` is the task's own `taskKey`, matching what the old app sent —
   * the "ID Type" select an identity task shows is a gate on the upload button
   * there too, never part of the payload.
   *
   * Returns the new document's id, or throws the message the API returned in its
   * place (a per-file failure rides back at HTTP 200, see `readUploadedId`).
   */
  async uploadAttachment(
    loanId: string,
    taskId: string,
    input: { documentType: string; file: File },
    signal?: AbortSignal,
  ): Promise<string> {
    const items: BpUploadTaskDocumentItem[] = [
      {
        documentType: input.documentType,
        fileName: input.file.name,
        fileContent: await fileToBase64(input.file),
      },
    ];
    const results = await apiClient.post<BpUploadTaskAttachmentResponse[]>(
      `${BASE}/${loanId}/tasks/${taskId}/upload`,
      items,
      { signal },
    );
    return readUploadedId(results?.[0]?.AttachmentId, `Upload failed for "${input.file.name}".`);
  },
};
